import { logger } from "@/utils/logger";
/**
 * 用户认证工具 - 迁移自原系统的认证逻辑
 * 支持用户ID加密、URL参数处理、Cookie管理
 */

// 原系统常量
const URL_PARAMETER_NAME = "jwtauth05";
const ENCODE_ADD_STRING = "gezhixiongdetaolixitong";

// 字符串反转函数
function reverse(s: string): string {
  if (typeof s !== "string") return "";
  return [...s].reverse().join("");
}

/**
 * Base64编码（兼容原系统格式）
 */
export function base64Encode(codeStr: string): string {
  const str =
    codeStr.slice(0, codeStr.length / 2) + ENCODE_ADD_STRING + codeStr.slice(codeStr.length / 2);

  if (typeof Buffer === "function") {
    const binaryData = Buffer.from(str, "utf8");
    return binaryData.toString("base64");
  }

  if (typeof btoa === "function") {
    const encode = encodeURI(str);
    return btoa(encode);
  }

  throw new Error("当前JS运行环境既不支持Buffer也不支持btoa");
}

/**
 * Base64解码（兼容原系统格式）
 */
export function base64Decode(str: string): string {
  if (!str) return "";

  try {
    // 清理和修正base64字符串
    let cleanStr = str.trim();

    // 移除可能的URL编码
    cleanStr = decodeURIComponent(cleanStr);

    // 确保base64字符串长度是4的倍数
    while (cleanStr.length % 4) {
      cleanStr += "=";
    }

    // 移除非base64字符
    cleanStr = cleanStr.replace(/[^A-Za-z0-9+/=]/g, "");

    if (typeof Buffer === "function") {
      const binaryData = Buffer.from(cleanStr, "base64");
      const enStr = binaryData.toString("utf8");
      return enStr.replace(ENCODE_ADD_STRING, "");
    }

    if (typeof atob === "function") {
      const decode = atob(cleanStr);
      const decodeStr = decodeURI(decode);
      return decodeStr.replace(ENCODE_ADD_STRING, "");
    }

    throw new Error("当前JS运行环境既不支持Buffer也不支持atob");
  } catch (error) {
    logger.warn("Base64解码失败，返回空字符串:", error);
    return "";
  }
}

/**
 * 构建URL参数（用于用户ID）
 */
export function buildUrlParameter(userId: string): { key: string; value: string } {
  const data: Record<string, string> = {};
  data[new Date().getMilliseconds().toString()] = userId;
  const jsonData = JSON.stringify(data);

  let value = base64Encode(jsonData);
  value = reverse(value);
  value = encodeURIComponent(value);

  return { key: URL_PARAMETER_NAME, value };
}

/**
 * 从URL解析用户ID
 */
export function parseUserIdFromUrl(url: string): string {
  const params = getUrlParameters(url);
  let value = params[URL_PARAMETER_NAME];

  if (value.indexOf("#") !== -1) {
    value = value.substr(0, value.indexOf("#"));
  }

  value = reverse(value);
  value = base64Decode(value);

  const data = JSON.parse(value);
  return data[Object.keys(data)[0]];
}

/**
 * 获取URL参数
 */
export function getUrlParameters(url: string): Record<string, string> {
  if (url.indexOf("?") === -1) return {};

  const queryString = url.substr(url.indexOf("?") + 1);
  const params = queryString.split("&");
  const result: Record<string, string> = {};

  if (queryString === "") return {};

  for (const param of params) {
    const [key, value] = param.split("=", 2);
    if (key) {
      result[key] = value ? decodeURIComponent(value.replace(/\+/g, " ")) : "";
    }
  }

  return result;
}

/**
 * 获取URL锚点
 */
export function getUrlAnchor(url: string): string {
  if (url.indexOf("#") === -1) return "";

  const first = url.lastIndexOf("/");
  const last = url.indexOf("?");

  const anchor = last !== -1 ? url.substring(first + 1, last) : url.substr(first + 1);
  return anchor || "";
}

/**
 * Cookie操作工具
 */
export class CookieManager {
  /**
   * 设置Cookie
   */
  static setCookie(name: string, value: string, expires?: number): void {
    let cookieString = `${name}=${value}; path=/`;

    if (expires) {
      const date = new Date();
      date.setTime(date.getTime() + expires * 24 * 60 * 60 * 1000);
      cookieString += `; expires=${date.toUTCString()}`;
    }

    document.cookie = cookieString;
  }

  /**
   * 获取Cookie
   */
  static getCookie(name: string): string | null {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(";");

    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length);
      }
    }

    return null;
  }

  /**
   * 删除Cookie
   */
  static removeCookie(name: string): void {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
  }
}

/**
 * Token管理
 */
export class TokenManager {
  private static readonly USERNAME_KEY = "username";
  private static readonly TOKEN_KEY = "token";
  private static readonly PERMISSIONS_KEY = "permissions";

  /**
   * 保存用户凭据
   */
  static saveCredentials(username: string, token: string, expires = 30): void {
    CookieManager.setCookie(this.USERNAME_KEY, base64Encode(username), expires);
    CookieManager.setCookie(this.TOKEN_KEY, base64Encode(token), expires);
  }

  /**
   * 保存用户权限（与老项目兼容）
   */
  static savePermissions(permissions: string[], expires = 30): void {
    const permissionsString = permissions.join(";");
    CookieManager.setCookie(this.PERMISSIONS_KEY, base64Encode(permissionsString), expires);
  }

  /**
   * 获取用户名
   */
  static getUsername(): string | null {
    const encoded = CookieManager.getCookie(this.USERNAME_KEY);
    return encoded ? base64Decode(encoded) : null;
  }

  /**
   * 获取Token
   */
  static getToken(): string | null {
    const encoded = CookieManager.getCookie(this.TOKEN_KEY);
    return encoded ? base64Decode(encoded) : null;
  }

  /**
   * 获取权限列表（与老项目兼容）
   */
  static getPermissions(): string[] {
    const encoded = CookieManager.getCookie(this.PERMISSIONS_KEY);
    if (!encoded) return [];

    try {
      const permissionsString = base64Decode(encoded);
      return permissionsString ? permissionsString.split(";").filter(Boolean) : [];
    } catch (error) {
      logger.warn("解析权限失败:", error);
      return [];
    }
  }

  /**
   * 清除凭据
   */
  static clearCredentials(): void {
    CookieManager.removeCookie(this.USERNAME_KEY);
    CookieManager.removeCookie(this.TOKEN_KEY);
    CookieManager.removeCookie(this.PERMISSIONS_KEY);
  }

  /**
   * 检查是否已登录
   */
  static isAuthenticated(): boolean {
    return !!(this.getUsername() && this.getToken());
  }
}

// 导出常用函数
export default {
  base64Encode,
  base64Decode,
  buildUrlParameter,
  parseUserIdFromUrl,
  getUrlParameters,
  getUrlAnchor,
  CookieManager,
  TokenManager,
};
